import { useMatch, useNavigate } from "react-router-dom";
import type { DeepPartial, Product, ProductType } from "@jasper/client-models";
import { useOrderById } from "@jasper/use-cases";
import {
  ADD_PRODUCT_ROUTE_PATTERN,
  ORDER_INFO_ROUTE_PATTERN,
  ORDER_SUMMARY_ROUTE_PATTERN,
  PRODUCT_STEP_ROUTE_PATTERN,
} from "../routePatterns";
import {
  buildAddProductPath,
  buildOrderConfirmationPath,
  buildOrderInfoPath,
  buildOrderSummaryPath,
  buildPreselectedTeethSearch,
  buildProductPath,
  buildProductStepPath,
} from "../paths";
import { getStepsForProduct } from "../registry";

export type OrderWorkflowProduct = {
  id: string;
  type: ProductType;
  items?: DeepPartial<Product>["items"];
};

export type OrderWorkflowLocation =
  | { phase: "order-info" }
  | { phase: "add-product" }
  | { phase: "summary" }
  | { phase: "product"; productId: string; step: string };
export type UseOrderWorkflowArgs = {
  orderId: string;
  bulkProductIds?: ReadonlyArray<string>;
};

interface Phase {
  phase: OrderWorkflowLocation["phase"];
  path: string;
  isCurrentPhase: boolean;
}

export type OrderWorkflow = {
  goBack: () => void;
  goNext: (
    product?: OrderWorkflowProduct,
    preselectedTeeth?: ReadonlyArray<number>
  ) => void;
};

export function useOrderWorkflow({
  orderId,
  bulkProductIds = [],
}: UseOrderWorkflowArgs): OrderWorkflow {
  const { order } = useOrderById({ orderId });
  const products = order?.products ?? [];

  const navigate = useNavigate();

  const orderInfoMatch = useMatch(ORDER_INFO_ROUTE_PATTERN);
  const productStepMatch = useMatch(PRODUCT_STEP_ROUTE_PATTERN);
  const summaryMatch = useMatch(ORDER_SUMMARY_ROUTE_PATTERN);
  const addProductMatch = useMatch(ADD_PRODUCT_ROUTE_PATTERN);

  const go = (path: string) => {
    navigate(path);
  };

  const bulkProductFollowerIds = new Set(
    bulkProductIds.filter(
      productId => productId !== productStepMatch?.params.productId
    )
  );

  const productPaths = products
    .filter((product): product is NonNullable<typeof product> =>
      Boolean(product)
    )
    .filter(product => !bulkProductFollowerIds.has(product.id))
    .map(product => ({
      path: buildProductPath(orderId, product.id),
      productId: product.id,
    }));

  const sequence: Phase[] = [
    {
      phase: "order-info",
      path: buildOrderInfoPath(orderId),
      isCurrentPhase: !!orderInfoMatch,
    },
    ...(productPaths.length > 0
      ? productPaths.map(({ path, productId }) => ({
          phase: "product" as const,
          path,
          isCurrentPhase: productStepMatch?.params.productId === productId,
        }))
      : [
          {
            phase: "add-product" as const,
            path: buildAddProductPath(orderId),
            isCurrentPhase: !!addProductMatch,
          },
        ]),
    {
      phase: "summary",
      path: buildOrderSummaryPath(orderId),
      isCurrentPhase: !!summaryMatch,
    },
  ];

  const currentPhaseIndex = sequence.findIndex(phase => phase.isCurrentPhase);

  const goToProduct = (
    product: OrderWorkflowProduct,
    preselectedTeeth?: ReadonlyArray<number>
  ) => {
    const [firstProductStep] = getStepsForProduct(product);
    if (firstProductStep) {
      go(
        `${buildProductStepPath(
          orderId,
          product.id,
          firstProductStep.key
        )}${buildPreselectedTeethSearch(preselectedTeeth)}`
      );
    }
  };

  const goToIndex = (
    index: number,
    preselectedTeeth?: ReadonlyArray<number>
  ) => {
    const target = sequence[index];
    if (target) {
      go(`${target.path}${buildPreselectedTeethSearch(preselectedTeeth)}`);
    }
  };

  return {
    goBack: () => {
      if (currentPhaseIndex > 0) {
        goToIndex(currentPhaseIndex - 1);
      }

      if (currentPhaseIndex === -1) {
        goToIndex(0);
      }
    },
    goNext: (product, preselectedTeeth) => {
      if (currentPhaseIndex === sequence.length - 1) {
        go(buildOrderConfirmationPath(orderId));
        return;
      }

      if (!!addProductMatch && product) {
        goToProduct(product, preselectedTeeth);
        return;
      }

      if (currentPhaseIndex === -1) {
        goToIndex(0, preselectedTeeth);
        return;
      }

      goToIndex(currentPhaseIndex + 1, preselectedTeeth);
    },
  };
}
